import { API_ID_MAP, ApiId, CACHE_CONFIG_TYPES, getApiIdMap } from '@intigral/constant';
import { APP_TYPE, AppType } from '@intigral/constant/common';
import { set, get } from '@intigral/utils/lib/browser-storage/localStorage';
import { log } from '@intigral/utils/lib/logger';
import { ApiConfig } from '../../core/entities/ApiConfig';
import { Placeholder } from '../../core/entities/CmsConfig';
import { IHttpAdapter } from '../../entity/interface/iHttpAdapter';
import { localConfig } from './localeConfigMapper';

export interface IApiPathManager {
    getPath(internalId: ApiId): string;
    getByDatasource(datasource: string): string;
    getApiConfigs(): ApiConfig[];
    replacePlaceholders(url: string, placeholders?: Map<string, string> | null): string;
    getParams(internalId: ApiId): any;
    getParamsByDatasource(datasource: string): any;
}

class ApiPathManager implements IApiPathManager {
    private appType: AppType = APP_TYPE.SLP_MLP;
    private apiConfigs: ApiConfig[] = [];
    private placeholders: Map<string, string> = new Map();

    private constructor(appType: AppType) {
        this.appType = appType;
    }

    private async initialize(apis: ApiConfig[], placeholders: Placeholder[]): Promise<void> {
        try {
            // Initialize placeholders
            placeholders.forEach((placeholder: { key: string; value: string }) => {
                this.placeholders.set(placeholder.key, placeholder.value);
            });

            // Process and store API configs
            this.apiConfigs = apis.map((api: ApiConfig) => ({
                ...api,
                url: this.replacePlaceholders(
                    api?.urlV6 || api?.urlV4 || api?.urlV3 || api?.urlV2 || api.newUrl || api.url
                ),
            }));
            this.apiConfigs.push(...localConfig.localApis);
            this.validateApiIdMap();
            //log.debug('API paths initialized from CMS:', this.apiConfigs);
        } catch (error) {
            log.error('Failed to initialize API paths from CMS:', error);
        }
    }

    static async createFromCMS(
        appType: AppType = APP_TYPE.WCP,
        cmsConfigUrl: string,
        http: IHttpAdapter
    ): Promise<{ instance: ApiPathManager; configuration: any }> {
        const instance = new ApiPathManager(appType);
        // const http = container.resolve<IHttpAdapter>(DI.UTILS.IHttpAdapter);
        try {
            //  Fetch configuration from CMS - will be saved to React Query by caller
            const response = await http.get<any>(cmsConfigUrl, { withCredentials: false });
            const dataObject = response.data;
            const cmsDetailsConfig = dataObject.data;

            // Still store version for potential future use
            const baseUrl = process.env['NX_PUBLIC_GATEWAY_API_BASE_URL'];
            const appKey = process.env['NX_PUBLIC_APP_KEY'];
            const revisionApiUrl = `${baseUrl}/v1/config/revision/${appKey}`;
            const revisionData = await http.get<any>(revisionApiUrl, { withCredentials: false });
            const currentVersion = revisionData?.data?.data?.version;
            set(CACHE_CONFIG_TYPES.version, currentVersion);

            await instance.initialize(cmsDetailsConfig.apis, cmsDetailsConfig.placeholders);

            // Return both the instance and the configuration data
            return { instance, configuration: cmsDetailsConfig };
        } catch (error) {
            log.error('Failed to initialize API paths from CMS:', error);
            throw error;
        }
    }

    static async createFromLocal(
        appType: AppType = APP_TYPE.SLP_MLP,
        localConfig: {
            apis: ApiConfig[];
            placeholders: Placeholder[];
        }
    ): Promise<ApiPathManager> {
        const instance = new ApiPathManager(appType);
        try {
            await instance.initialize(localConfig.apis, localConfig.placeholders);
        } catch (error) {
            log.error('Failed to initialize API paths from local config:', error);
        }

        return instance;
    }

    private validateApiIdMap(): void {
        const loadedIds = new Set(this.apiConfigs.map((config) => config.id));
        for (const [internalId, externalId] of Object.entries(API_ID_MAP)) {
            if (!loadedIds.has(externalId)) {
                // log.warn(`Mapped API ID '${externalId}' (internal: '${internalId}') not found in loaded configuration`);
            }
        }
    }

    replacePlaceholders(url: string, placeholders?: Map<string, string> | null): string {
        return url.replace(/\$?{(\w+)}/g, (match, key) => {
            const value = placeholders == null ? this.placeholders.get(key) : placeholders.get(key);
            return value !== undefined ? value : match;
        });
    }

    getPath(internalId: ApiId): string {
        const apiIdMap = getApiIdMap(this.appType);
        const externalId = apiIdMap[internalId];
        if (!externalId) {
            throw new Error(`No external ID mapping found for internal ID: ${internalId}`);
        }
        const config = this.apiConfigs.find((api) => api.id === externalId);
        if (!config) {
            if (externalId === 'layout_gateway_screen') {
                return this.replacePlaceholders(
                    '${NX_PUBLIC_GATEWAY_API_BASE_URL}/v1/{apiKey}/layout/{layoutName}/{screenName}?platform={platformType}&deviceType={deviceType}'
                );
            }
            throw new Error(`API path not found for key: ${internalId} (external: ${externalId})`);
        }
        return config.url;
    }

    getParams(internalId: ApiId): any {
        const apiIdMap = getApiIdMap(this.appType);
        const externalId = apiIdMap[internalId];
        if (!externalId) {
            throw new Error(`No external ID mapping found for internal ID: ${internalId}`);
        }
        const config = this.apiConfigs.find((api) => api.id === externalId);
        if (!config) {
            throw new Error(`API path not found for key: ${internalId} (external: ${externalId})`);
        }
        return config.params;
    }

    getByDatasource(datasource: string): string {
        // const apiIdMap = getApiIdMap(this.appType);
        // const externalId = apiIdMap[internalId];
        // if (!externalId) {
        //     throw new Error(`No external ID mapping found for internal ID: ${internalId}`);
        // }
        const config = this.apiConfigs.find((api) => api.id === datasource);
        if (!config) {
            throw new Error(`API path not found for key: ${datasource})`);
        }
        return config.url;
    }

    getApiConfigs(): ApiConfig[] {
        return this.apiConfigs;
    }

    getParamsByDatasource(datasource: string): any {
        const config = this.apiConfigs.find((api) => api.id === datasource);
        if (!config) {
            throw new Error(`API path not found for key: ${datasource}`);
        }
        return config.params;
    }
}

export default ApiPathManager;
